/* eslint-disable sonarjs/cognitive-complexity */
import { getProductCategoryData } from '@/lib/cms/getProductCategoryData'
import { getProducts } from '@/lib/cms/getProducts'
import { getProductsBanner } from '@/lib/cms/getProductsBanner'
import type { Alpha2CountryCode } from '@/lib/types/alpha2CountryCode'
import { assignPricingToVariation } from '@/lib/utils/api/assignPricingToVariation'
import type { SupportedLocale } from '@/lib/utils/localization/getFlattenedLocales'

import type { CMSError } from '@/lib/cms/types/CMSError'
import { jeeves } from '../Jeeves'

type Props = {
  locale: SupportedLocale
  alpha2: Alpha2CountryCode
  category: string
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const getGridPageData = async ({ locale, alpha2, category }: Props) => {
  const results = await Promise.allSettled([
    getProductsBanner({ locale }),
    getProductCategoryData({ locale, categorySlug: category }),
    getProducts({ locale }),
  ])

  const productsBannerImageData =
    results[0].status === 'fulfilled' ? results[0].value : null
  const productCategoryData =
    results[1].status === 'fulfilled' ? results[1].value : null
  const productsResult =
    results[2].status === 'fulfilled' ? results[2].value : null

  if (!productsResult || 'error' in productsResult) {
    return {
      productsBannerImageData,
      productCategoryData: null,
      products: productsResult,
    }
  }

  let filteredProducts = productsResult.filter((product) => product.name)

  if (category !== '/') {
    filteredProducts = productsResult.filter(
      (product) => product.categories?.some((c) => c.slug === category),
    )
  }

  if (filteredProducts.length === 0) {
    const noProductsError: CMSError = {
      message: `No products found for this category (${category})`,
      code: 'NO_PRODUCTS_FOUND',
      timestamp: new Date().toISOString(),
      source: 'cosmic',
    }

    return {
      productsBannerImageData,
      productCategoryData,
      products: {
        error: noProductsError,
      },
    }
  }

  const allProductSkus = filteredProducts
    .flatMap(
      (product) =>
        product.variations
          ?.map((variation) => {
            const { sku } = variation
            return sku || ''
          })
          .filter((sku: string) => sku.trim() !== '') || [],
    )
    .filter(Boolean)

  if (allProductSkus.length === 0) {
    const noProductsError: CMSError = {
      message: 'No products found',
      code: 'NO_PRODUCTS_FOUND',
      timestamp: new Date().toISOString(),
      source: 'cosmic',
    }

    return {
      productsBannerImageData,
      productCategoryData,
      products: {
        error: noProductsError,
      },
    }
  }

  const pricingData = await jeeves.getProductPricing({
    market: alpha2,
    skus: allProductSkus,
  })

  if (!pricingData) {
    const pricingError: CMSError = {
      message: 'Failed to fetch pricing',
      code: 'JEEVES_PRICING_ERROR',
      timestamp: new Date().toISOString(),
      source: 'jeeves',
    }

    return {
      productsBannerImageData,
      productCategoryData,
      products: {
        error: pricingError,
      },
    }
  }

  const productsWithAssignedPricing = filteredProducts
    .map((product) =>
      assignPricingToVariation({
        productData: product,
        pricingData,
      }),
    )
    .filter((product) => product !== null)

  return {
    productsBannerImageData,
    productCategoryData,
    products: productsWithAssignedPricing,
  }
}
